import React, { useState, useEffect } from 'react';
import {
    Users, Building2, Package, TrendingUp,
    FileCheck, Activity, RefreshCw, Clock,
    ArrowUp, ArrowDown, Shield, Briefcase
} from 'lucide-react';
import { dashboardService } from '../services/dashboardService';
import { useAuth } from '../context/AuthContext';
import DashboardDataQualityWidget from './data-quality/DashboardDataQualityWidget';

const DashboardContent = () => {
    const { hasPermission } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);

    // Fetch dashboard statistics
    const fetchStats = async (isRefresh = false) => {
        try {
            if (isRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }

            const response = await dashboardService.getStats();

            if (response.success && response.data) {
                setStats(response.data);
                setLastUpdate(new Date());
                setError(null);
            }
        } catch (err) {
            console.error('Error fetching dashboard stats:', err);
            setError('Failed to load dashboard statistics');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchStats();

        // Auto-refresh every 5 minutes
        const interval = setInterval(() => {
            fetchStats(true);
        }, 300000); // 5 minutes

        return () => clearInterval(interval);
    }, []);

    // Format number with commas
    const formatNumber = (num) => {
        if (num === null || num === undefined) return '0';
        return new Intl.NumberFormat().format(num);
    };

    // Format percentage
    const formatPercentage = (num) => {
        if (num === null || num === undefined) return '0%';
        return `${num.toFixed(1)}%`;
    };

    // Format growth rate with icon
    const GrowthIndicator = ({ value }) => {
        if (!value || value === 0) return <span className="text-gray-500">-</span>;

        const isPositive = value > 0;
        return (
            <span className={`flex items-center gap-1 text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {isPositive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                {formatPercentage(Math.abs(value))}
            </span>
        );
    };

    // Stat Card Component
    const StatCard = ({ title, value, subtitle, icon: Icon, color, change, loading }) => {
        const colorClasses = {
            blue: 'bg-blue-100 text-blue-600',
            green: 'bg-green-100 text-green-600',
            purple: 'bg-purple-100 text-purple-600',
            yellow: 'bg-yellow-100 text-yellow-600',
            red: 'bg-red-100 text-red-600',
            indigo: 'bg-indigo-100 text-indigo-600',
        };

        return (
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
                        <Icon className="w-6 h-6" />
                    </div>
                    {change !== undefined && <GrowthIndicator value={change} />}
                </div>
                <div>
                    <p className="text-sm text-gray-500 mb-1">{title}</p>
                    {loading ? (
                        <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
                    ) : (
                        <>
                            <p className="text-2xl font-bold text-gray-900">{value}</p>
                            {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
                        </>
                    )}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <h2 className="text-2xl font-semibold text-gray-900">Dashboard Overview</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[...Array(8)].map((_, i) => (
                        <div key={i} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                            <div className="animate-pulse">
                                <div className="h-12 w-12 bg-gray-200 rounded-lg mb-4"></div>
                                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                                <div className="h-8 bg-gray-200 rounded"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <p className="text-red-600">{error}</p>
                <button
                    onClick={() => fetchStats()}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-semibold text-gray-900">Dashboard Overview</h2>
                    {lastUpdate && (
                        <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Last updated: {lastUpdate.toLocaleTimeString()}
                        </p>
                    )}
                </div>
                <button
                    onClick={() => fetchStats(true)}
                    disabled={refreshing}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center gap-2 disabled:opacity-50"
                >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
            </div>

            {/* Main Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    title="Total Clients"
                    value={formatNumber(stats?.totalClients)}
                    change={stats?.clientMonthlyGrowth}
                    icon={Users}
                    color="blue"
                    subtitle={`${formatNumber(stats?.retailClients)} Retail, ${formatNumber(stats?.corporateClients)} Corporate`}
                    loading={refreshing}
                />
                <StatCard
                    title="Active Companies"
                    value={formatNumber(stats?.activeCompanies)}
                    icon={Building2}
                    color="green"
                    subtitle={`${formatNumber(stats?.totalCompanies)} total companies`}
                    loading={refreshing}
                />
                <StatCard
                    title="Products"
                    value={formatNumber(stats?.activeProducts)}
                    icon={Package}
                    color="purple"
                    subtitle={`${formatNumber(stats?.totalProducts)} total products`}
                    loading={refreshing}
                />
                <StatCard
                    title="Cross-sell Rate"
                    value={formatPercentage(stats?.crossSellRate)}
                    icon={TrendingUp}
                    color="yellow"
                    subtitle={`${formatNumber(stats?.clientsWithConsent)} consent given`}
                    loading={refreshing}
                />
            </div>

            {/* Secondary Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    title="Active Clients"
                    value={formatNumber(stats?.activeClients)}
                    icon={Activity}
                    color="indigo"
                    subtitle="Currently active"
                    loading={refreshing}
                />
                <StatCard
                    title="New This Month"
                    value={formatNumber(stats?.newClientsThisMonth)}
                    icon={Users}
                    color="green"
                    subtitle="New client registrations"
                    loading={refreshing}
                />
                <StatCard
                    title="Multi-Product Clients"
                    value={formatNumber(stats?.clientsWithMultipleProducts)}
                    icon={Briefcase}
                    color="purple"
                    subtitle="Have 2+ products"
                    loading={refreshing}
                />
                <StatCard
                    title="Avg Products/Client"
                    value={stats?.averageProductsPerClient?.toFixed(2) || '0'}
                    icon={Package}
                    color="blue"
                    subtitle="Product penetration"
                    loading={refreshing}
                />
            </div>

            {/* Quick Insights */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Quick Insights
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-sm text-blue-600 font-medium">Most Popular Product</p>
                        <p className="text-lg font-semibold text-blue-900 mt-1">
                            {stats?.mostPopularProductName || 'N/A'}
                        </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                        <p className="text-sm text-green-600 font-medium">Companies with Products</p>
                        <p className="text-lg font-semibold text-green-900 mt-1">
                            {formatNumber(stats?.companiesWithProducts)} / {formatNumber(stats?.totalCompanies)}
                        </p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                        <p className="text-sm text-purple-600 font-medium">Client Growth Rate</p>
                        <p className="text-lg font-semibold text-purple-900 mt-1">
                            <GrowthIndicator value={stats?.clientMonthlyGrowth} />
                        </p>
                    </div>
                </div>
            </div>

            {/* Data Quality Widget */}
            {hasPermission('DATA_QUALITY:READ') && (
                <DashboardDataQualityWidget />
            )}
        </div>
    );
};

export default DashboardContent;
